import { Fragment, Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid';

import {
    deliveryReturnsTitle,
    deliveryTitle,
    methodNoteFreeShipping,
} from '../messages/pdpDetailsMessages';
import { PdpSharedDetailShipping } from '../PdpSharedDetails/PdpSharedDetailShipping';
import { PdpSharedDetailReturnPolicy } from '../PdpSharedDetails/PdpSharedDetailReturnPolicy';
import { PdpSharedBuyerProtectionBox } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionBox';
import { PdpSharedDepositPaymentDetail } from '../PdpSharedDetails/PdpSharedDepositPaymentDetail';
import { PdpSharedBuyerProtectionAuctions } from '../PdpSharedBuyerProtectionBox/PdpSharedBuyerProtectionAuctions';
import { PdpRespDetail } from './PdpRespDetail';
import { PdpSharedDetailCancelationPolicy } from '../shared/PdpSharedDetail/PdpSharedDetailCancelationPolicy';

import styles from './PdpRespDetails.scss';

const PdpRespPricingGuidanceLazy = lazy(() =>
    import(/* webpackChunkName: "PdpRespPricingGuidance" */ './PdpRespPricingGuidance')
);

function PdpRespShippingReturnsComponent({ currency, item, user, itemQuantity, isCondensedTab }) {
    const { seller, isListingMigrated, isAuctionEnabled, isDepositAvailable } = item;
    const { isBuyerCancelationPolicyVariant, userId, itemId, isClient } = useSelector(
        relay => relay?.relayVariables?.variables || {}
    );

    useEffect(() => {
        trackAbTestV2Variant('buyerCancelationPolicy');
    }, []);

    if (!seller?.isAccessible || isListingMigrated) {
        return null;
    }

    const returnPolicySectionHeader = (
        <span
            dangerouslySetInnerHTML={{
                __html: item?.returnPolicy?.buyerCopy?.returnPolicySectionHeader?.htmlCopy || '',
            }}
        />
    );

    const preLinkQuoteDetails = <Fragment>{methodNoteFreeShipping} </Fragment>;

    return (
        <div
            className={classNames(styles.wrapper, styles.shippingModule, {
                [styles.noMargin]: isCondensedTab,
            })}
            data-tn="pdp-details-shipping"
        >
            <HeadingLevel>
                {Heading => (
                    <>
                        <GridRow>
                            <GridCol desktop={4} mobile={6}>
                                {!isCondensedTab && (
                                    <Heading className={styles.header}>
                                        {deliveryReturnsTitle}
                                    </Heading>
                                )}
                                <ul className={styles.shippingReturnsRow}>
                                    {/* Shipping */}
                                    <PdpRespDetail
                                        title={deliveryTitle}
                                        dataTn="shipping"
                                        printHide
                                    >
                                        <PdpSharedDetailShipping
                                            currency={currency}
                                            userId={userId}
                                            itemId={itemId}
                                            isClient={isClient}
                                            item={item}
                                            user={user}
                                            preLinkQuoteDetails={preLinkQuoteDetails}
                                            displayInline
                                            itemQuantity={itemQuantity}
                                        />
                                    </PdpRespDetail>
                                    {/* Return Policy */}
                                    <PdpRespDetail
                                        title={returnPolicySectionHeader}
                                        dataTn="return-policy"
                                    >
                                        <PdpSharedDetailReturnPolicy
                                            userId={userId}
                                            itemId={itemId}
                                            item={item}
                                        />
                                    </PdpRespDetail>
                                </ul>
                            </GridCol>
                            <GridCol desktop={4} mobile={12}>
                                <div
                                    className={classNames(
                                        styles.printHide,
                                        styles.buyerProtectionRow,
                                        {
                                            [styles.marginTop]: !isCondensedTab,
                                        }
                                    )}
                                >
                                    {!isBuyerCancelationPolicyVariant && (
                                        <>
                                            {isDepositAvailable && (
                                                <div className={styles.depositRow}>
                                                    <PdpSharedDepositPaymentDetail
                                                        userId={userId}
                                                        item={item}
                                                        wrapper={PdpRespDetail}
                                                        linkClass={styles.depositLink}
                                                    />
                                                </div>
                                            )}
                                            {isAuctionEnabled && (
                                                <PdpSharedBuyerProtectionAuctions />
                                            )}
                                        </>
                                    )}

                                    <PdpSharedBuyerProtectionBox user={user} />

                                    {/* Cancelation Policy */}
                                    {isBuyerCancelationPolicyVariant && (
                                        <PdpSharedDetailCancelationPolicy item={item} />
                                    )}
                                </div>
                            </GridCol>
                            {isBuyerCancelationPolicyVariant && (
                                <GridCol desktop={4} mobile={6}>
                                    {isAuctionEnabled && <PdpSharedBuyerProtectionAuctions />}
                                    {isDepositAvailable && (
                                        <div className={styles.depositRow}>
                                            <PdpSharedDepositPaymentDetail
                                                userId={userId}
                                                item={item}
                                                wrapper={PdpRespDetail}
                                                linkClass={styles.depositLink}
                                            />
                                        </div>
                                    )}
                                </GridCol>
                            )}
                            {isClient && !isCondensedTab && (
                                <Suspense fallback="">
                                    <PdpRespPricingGuidanceLazy
                                        currency={currency}
                                        item={item}
                                        user={user}
                                    />
                                </Suspense>
                            )}
                        </GridRow>
                    </>
                )}
            </HeadingLevel>
        </div>
    );
}

PdpRespShippingReturnsComponent.propTypes = {
    currency: PropTypes.string,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    itemQuantity: PropTypes.number,
    isCondensedTab: PropTypes.bool,
};

export const PdpRespShippingReturns = createFragmentContainer(PdpRespShippingReturnsComponent, {
    user: graphql`
        fragment PdpRespShippingReturns_user on User {
            ...PdpSharedDetailShipping_user
            ...PdpSharedBuyerProtectionBox_user
            ...PdpRespPricingGuidance_user @include(if: $isClient)
        }
    `,
    item: graphql`
        fragment PdpRespShippingReturns_item on Item {
            ...PdpSharedDetailReturnPolicy_item
            ...PdpSharedDepositPaymentDetail_item
            ...PdpSharedDetailShipping_item
            ...PdpRespPricingGuidance_item @include(if: $isClient)
            ...PdpSharedDetailCancelationPolicy_item @include(if: $isBuyerCancelationPolicyVariant)
            isDepositAvailable(buyerId: $userId)
            returnPolicy(userId: $userId) {
                buyerCopy {
                    returnPolicySectionHeader {
                        htmlCopy
                    }
                }
            }
            seller {
                isAccessible
            }
            isAuctionEnabled
            isListingMigrated
        }
    `,
});
